import { ErrorProductColumns } from '@Arti-zen/package-backoffice'
import { CloudUpload, Download, Inventory } from '@mui/icons-material'
import { CircularProgress } from '@mui/material'
import { useAppSelector } from 'hooks/useStore'
import {
  CardDescription,
  CardLabel,
  CardsRow,
  ErrorMessage,
  ExportButton,
  FileInputLabel,
  HiddenInput,
  SectionGroup,
  SectionTitle,
  UploadCard,
} from 'modules/artisan/components/ArtisanUpload'
import { pages } from 'navigation/pages'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Theme } from 'theme/theme'
import { ApiArtizenBO } from 'utils/artizenConnector'
import ErrorColumns from './ErrorColumns'

export default function UploadProductsCards() {
  const { artisan } = useAppSelector((state) => state.artisan)
  const navigate = useNavigate()

  const [errorMessageImport, setErrorMessageImport] = useState<ErrorProductColumns>(null)
  const [errorMessageUpdate, setErrorMessageUpdate] = useState<ErrorProductColumns | string | null>(null)
  const [loadingExtract, setLoadingExtract] = useState<boolean>(false)

  const handleProductToCreateList = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setErrorMessageImport(null)
    setErrorMessageUpdate(null)

    const result = await ApiArtizenBO.products.previewImportProducts(String(artisan.id), file)

    if (result.success === false) {
      setErrorMessageImport(result.error)
      if (result?.error && 'error' in result.error) {
        console.error(result.error)
      }
      return
    }

    navigate(pages.connected.artisan.uploadProducts.navigate(+artisan.id), {
      state: {
        result,
      },
    })
  }

  const handleProductToModifyList = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setErrorMessageImport(null)
    setErrorMessageUpdate(null)

    const result = await ApiArtizenBO.products.previewModifyProducts(String(artisan.id), file)

    if (result.success === false) {
      setErrorMessageUpdate(result.error)
      if (result?.error && 'error' in result.error) {
        console.error(result.error)
      }
      return
    }
    navigate(pages.connected.artisan.uploadProducts.navigate(artisan.id), {
      state: {
        result,
      },
    })
  }

  const handleExtract = async () => {
    setLoadingExtract(true)
    const result = await ApiArtizenBO.products.extractProducts(String(artisan.id))
    setLoadingExtract(false)
    if (!result.success) {
      setErrorMessageUpdate(result.errorMessage)
      return
    }
    const blob = new Blob(['\uFEFF' + result.csv], {
      type: 'text/csv;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'produits.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const getErrorMessage = (error: ErrorProductColumns) => {
    switch (error.type) {
      case 'EMPTY_ARRAY':
        return 'Le fichier envoyé contient un tableau vide.'
      case 'INCORRECT_COLUMNS':
        return `Les colonnes du fichiers sont incorrectes.${error.missingColumns.length ? `Colonnes manquantes : ${error.missingColumns.join(', ')}.` : ''}${error.extraColumns.length ? `Colonnes en trop : ${error.extraColumns.join(', ')}.` : ''}`
      case 'PROCESSING_ERROR':
        return `Le fichier n'a pas pu être chargé. `
    }
  }
  return (
    <>
      <SectionGroup>
        <SectionTitle>
          <Inventory style={{ color: Theme.colors.blueArtizen }} />
          Produits
        </SectionTitle>
        <CardsRow>
          <UploadCard>
            <CardLabel>Ajouter des produits via un fichier CSV</CardLabel>
            <CardDescription>Vérifiez les produits avant de les importer</CardDescription>
            {errorMessageImport && errorMessageImport.type !== 'INCORRECT_COLUMNS' && (
              <ErrorMessage>{getErrorMessage(errorMessageImport)}</ErrorMessage>
            )}
            <FileInputLabel>
              <CloudUpload fontSize="small" />
              Choisir un fichier CSV
              <HiddenInput type="file" accept=".csv,.xlsx" onChange={handleProductToCreateList} />
            </FileInputLabel>
          </UploadCard>

          <UploadCard>
            <CardLabel>Modifier des produits via un fichier CSV</CardLabel>
            <CardDescription>Vérifiez les produits avant de les modifier</CardDescription>
            {errorMessageUpdate && typeof errorMessageUpdate === 'string' && (
              <ErrorMessage>{errorMessageUpdate}</ErrorMessage>
            )}
            {errorMessageUpdate &&
              typeof errorMessageUpdate !== 'string' &&
              errorMessageUpdate?.type !== 'INCORRECT_COLUMNS' && (
                <ErrorMessage>{getErrorMessage(errorMessageUpdate)}</ErrorMessage>
              )}
            <ExportButton onClick={handleExtract}>
              <Download fontSize="small" />
              Importer produits existants en CSV
              {loadingExtract && <CircularProgress size={15} color="inherit" />}
            </ExportButton>
            <FileInputLabel>
              <CloudUpload fontSize="small" />
              Choisir un fichier CSV
              <HiddenInput type="file" accept=".csv,.xlsx" onChange={handleProductToModifyList} />
            </FileInputLabel>
          </UploadCard>
        </CardsRow>
      </SectionGroup>

      {errorMessageImport?.type === 'INCORRECT_COLUMNS' && (
        <ErrorColumns
          missingColumns={errorMessageImport.missingColumns}
          extraColumns={errorMessageImport.extraColumns}
          onModify={false}
          productsRaw={errorMessageImport.raw}
          onClose={() => setErrorMessageImport(null)}
        />
      )}
      {errorMessageUpdate &&
        typeof errorMessageUpdate !== 'string' &&
        errorMessageUpdate.type === 'INCORRECT_COLUMNS' && (
          <ErrorColumns
            missingColumns={errorMessageUpdate.missingColumns}
            extraColumns={errorMessageUpdate.extraColumns}
            onModify={true}
            productsRaw={errorMessageUpdate.raw}
            onClose={() => setErrorMessageUpdate(null)}
          />
        )}
    </>
  )
}
